import React, { useState } from 'react';
import {
  DateParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import moment from 'moment';
import {
  Grid,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';

import { AppBar, DebouncedTextField, Empty, Error, Loader } from '@/components';
import { OrderDirections, ORDER_DIRECTIONS } from '@/utils/order-directions';

import { ShiftsTable } from './components';
import {useLazyGetShiftsQuery} from "@/services/shift";

const ShiftsPage: React.FC = () => {
  const [{ search, order_direction, page, date_from, date_to }, setQuery] =
      useQueryParams({
        search: withDefault(StringParam, ''),
        page: withDefault(NumberParam, 1),
        order_direction: withDefault(StringParam, OrderDirections.DESC),
        date_from: withDefault(DateParam, moment().toDate()),
        date_to: withDefault(DateParam, moment().toDate()),
      });

  const [trigger, { data: shifts, isLoading, isFetching, isError, isSuccess }] =
      useLazyGetShiftsQuery();

  const [hasSearched, setHasSearched] = useState(false);

  const buildRequest = (overridePage = page) => ({
    params: {
      search: search || null,
      order_direction,
      page: overridePage,
      order_by: 'id',
      date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
      date_to: date_to ? moment(date_to).format('YYYY-MM-DD') : null,
    },
  });

  const onSearch = () => {
    setQuery({ page: 1 });
    setHasSearched(true);
    trigger(buildRequest(1));
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    trigger(buildRequest(newPage));
  };

  return (
      <>
        <AppBar title="Смены" />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="flex-start" spacing={3}>
              <Grid container spacing={2}>
                {/* Поиск */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DebouncedTextField
                      type="search"
                      name="search"
                      label="Поиск"
                      placeholder="Введите значение"
                      value={search}
                      onChange={e => setQuery({ search: e.target.value, page: 1 })}
                      variant="filled"
                      fullWidth
                      size="small"
                  />
                </Grid>

                {/* Сортировка */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      label="Сортировать по"
                      value={order_direction}
                      disabled={isLoading}
                      onChange={e => setQuery({ order_direction: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    {ORDER_DIRECTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Дата от */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата от"
                      value={date_from}
                      minDate={moment(date_to).subtract(6, 'months')}
                      maxDate={moment(date_to)}
                      onChange={v =>
                          setQuery({ date_from: v ? moment(v).toDate() : undefined })
                      }
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>

                {/* Дата до */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата до"
                      value={date_to}
                      maxDate={moment()}
                      onChange={v =>
                          setQuery({ date_to: v ? moment(v).toDate() : undefined })
                      }
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>

                {/* Кнопка «Поиск» */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <LoadingButton
                      loading={isFetching}
                      disabled={isFetching}
                      onClick={onSearch}
                      size="large"
                      variant="contained"
                      fullWidth
                  >
                    Поиск
                  </LoadingButton>
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          <Grid item lg={9} xs={12}>
            {!hasSearched ? (
                <Empty text="Нажмите «Поиск», чтобы увидеть данные" />
            ) : isLoading ? (
                <Loader />
            ) : isError ? (
                <Error text="Произошла ошибка с загрузкой смен! Пожалуйста, попробуйте позже" />
            ) : isSuccess && shifts?.data?.length ? (
                <ShiftsTable
                    list={shifts}
                    page={page}
                    disabled={isFetching}
                    onChangePage={onChangePage}
                />
            ) : (
                <Empty text="Смены отсутствуют" />
            )}
          </Grid>
        </Grid>
      </>
  );
};

export default ShiftsPage;
